import {isEmpty, merge} from 'lodash';
import AssessmentResult from '../../../values/AssessmentResult';
import Assessment from '../assessment';
import {MAIN_CONTENT_POINTS, RELATED_KEYWORDS_DENSITY_ID} from '@axyseo/const/analysis';

/**
 * Represents the assessment that checks whether there are enough transition words in the text.
 */
export default class RelatedKeywordsDensityAssessment extends Assessment {
  /**
   * Sets the identifier and the config.
   *
   * @param {object} config The configuration to use.
   *
   * @returns {void}
   */
  constructor(config = {}) {
    super();

    const defaultConfig = {
      id: RELATED_KEYWORDS_DENSITY_ID,
      fixPosition: 'description',
      ctaType: 'fix',
      docUrl:
        'https://docs.avada.io/seo-suite-help-center/seo-on-page-audit/checklist#related-keyword-density',
      priority: 'high',
      title: 'Related keywords density',
      content: {
        good: 'Related keywords density optimized',
        bad:
          'Use each secondary keyword from 0.5% to 1%. Place where it reads naturally.',
        improve: ''
      }
    };

    this.identifier = RELATED_KEYWORDS_DENSITY_ID;
    this._config = merge(defaultConfig, config);
  }

  /**
   *
   * @param data
   * @param relatedKeywords
   * @param hasDescription
   * @returns {{score: number, status: string, words}}
   */
  calculateResult(data, relatedKeywords, hasDescription) {
    const {words} = relatedKeywords;
    let status = 'good';
    const totalWords = Object.values(words).filter(word => word.percentage === 0);
    if(!hasDescription || isEmpty(words) || totalWords.length > 0){
      status = 'bad';
    }
    const score = this.getScore(MAIN_CONTENT_POINTS, status);

    return {
      score,
      status,
      words
    };
  }

  /**
   *
   * @param paper
   * @param researcher
   * @returns {AssessmentResult}
   */
  getResult({paper, researcher}) {
    const relatedKeywords = researcher.getResearch('checkRelatedKeywords');
    const data = paper.getData();
    const hasDescription = paper.hasDescription();
    const result = this.calculateResult(data, relatedKeywords, hasDescription);
    const assessmentResult = new AssessmentResult({config: this._config});

    assessmentResult.setScore(result.score);
    assessmentResult.setStatus(result.status);
    assessmentResult.setData(result.words);

    return assessmentResult;
  }

  /**
   * Checks if the transition words assessment is applicable to the paper. Language-specific length requirements and methods of counting text length
   * may apply (e.g. for Japanese, the text should be counted in characters instead of words, which also makes the minimum required length higher).
   *
   * @param {Paper}       paper       The paper to check.
   * @param {Researcher}  researcher  The researcher object.
   *
   * @returns {boolean} Returns true if the language is available, the paper is not empty and the text is longer than the minimum required length.
   */
  isApplicable(paper, researcher) {
    return true;
  }
}
